"use client";

import { useState, useEffect } from "react";
import { X, ArrowLeft, Loader } from "lucide-react";
import Step1EventType from "./steps/Step1EventType";
import Step2EventDetails from "./steps/Step2EventDetails";
import Step3EventNeeds from "./steps/Step3EventNeeds";
import Step4Vendors from "./steps/Step4Vendors";
import Step5Summary from "./steps/Step5Summary";
import CommunityEventFinal from "./steps/CommunityEventFinal";
import CommercialEventSuccess from "./steps/CommercialEventSuccess";
import ServiceRequest from "./ServiceRequest";
import {
  addCommercialEvents,
  addCommunityEvents,
  addPersonalEvents,
  getBusinessByNeeds,
  getEventNeedsFromType,
  getEventTypes,
} from "../../services/events";
import { successToast,errorToast } from '../../utils/toasters'
import { handlePostRequest } from "../../hooks/api";
import CommercialEventFinal from "./steps/CommercialContactForm";
import { useNavigate } from "react-router-dom";
import { isAuthenticated } from "../../utils/helpers";

export default function EventForm({ onClose, pe }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [modalState, setModalState] = useState({ type: "none" });
  const [formData, setFormData] = useState({
    eventType: "",
    eventName: "",
    eventTypeDetail: "",
    date: "",
    venue: "",
    guests: 2,
    budget: 500,
    description: "",
    needs: [],
    vendors: [],
    startTime: "",
    endTime: "",

    contactInfo: {
      fullName: "",
      phoneNumber: "",
      state: "",
      addressLine1: "",
      addressLine2: "",
    },
    completed: false,
  });

  // Add back the showServiceRequest state that was removed
  const [showServiceRequest, setShowServiceRequest] = useState(false);

  const [eventTypes, setEventTypes] = useState(null);
  const [eventNeeds, setEventNeeds] = useState(null);
  const [business, setBusiness] = useState(null);

  // Check for pending event data on component mount
  useEffect(() => {
    const pendingData = localStorage.getItem("pendingEventData");
    if (pendingData && isAuthenticated()) {
      try {
        const parsedData = JSON.parse(pendingData);
        setFormData(parsedData);

        successToast("Completing your saved event posting...", {
          position: "top-right",
          autoClose: 3000,
        });

        // Submit the pending event data
        submitEvent(parsedData);
        localStorage.removeItem("pendingEventData");
      } catch (error) {
        console.error("Error parsing pending event data:", error);
        localStorage.removeItem("pendingEventData");
        errorToast("Failed to restore your saved event. Please try again.");
      }
    }
  }, []);

  useEffect(() => {
    async function eventTypeFetcher() {
      try {
        const eventTypes = await getEventTypes();
        setEventTypes(eventTypes.events);
      } catch (error) {
        console.error("Error while fetching event types", error);
        errorToast("Error while fetching event types");
      }
    }
    eventTypeFetcher();
  }, []);

  useEffect(() => {
    if (pe) {
      setFormData((prev) => ({
        ...prev,
        eventType: "Personal Events",
      }));
      setCurrentStep(2); // Skip event type selection, go to next step
    }
  }, [pe]);
  useEffect(() => {
    async function eventNeedsFetcher(eventTypeDetail) {
      try {
        const eventType = eventTypes.find(
          (e) => e.eventtypename == eventTypeDetail
        );
        const eventNeeds = await getEventNeedsFromType(eventType.id);
        setEventNeeds(eventNeeds.eventneeds);
      } catch (error) {
        console.error("Error while fetching event types", error);
        errorToast("Error while fetching event types");
      }
    }
    if (!formData.eventTypeDetail) {
      return;
    }
    eventNeedsFetcher(formData.eventTypeDetail);
  }, [formData.eventTypeDetail]);
  useEffect(() => {
    async function businessFetcher(payload) {
      try {
        const businessResponse = await getBusinessByNeeds(payload);
        setBusiness(businessResponse.business);
      } catch (error) {
        console.error("Error while fetching event types", error);
        errorToast("Error while fetching event types");
      }
    }
    if (!formData.needs || formData.needs.length == 0) {
      return;
    }
    businessFetcher(formData.needs);
  }, [formData.needs]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        if (modalState.type !== "none") {
          setModalState({ type: "none" });
        } else {
          onClose();
        }
      }
    };
    window.addEventListener("keydown", handleEsc);

    // Prevent scrolling on body when modal is open
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "auto";
    };
  }, [onClose, modalState]);

  const handleNext = () => {
    // For Community Events, go from step 2 directly to final step
    if (formData.eventType === "Community Events" && currentStep === 2) {
      setCurrentStep(98); // Use 98 to represent the community event final step
    } else if (formData.eventType === "Commercial Event" && currentStep === 3) {
      // For Commercial Events, follow steps 1, 2, 3 (contact form), 4 (success)
      setCurrentStep(99); // Use 99 to represent the commercial event success step
    } else if (
      currentStep === 3 &&
      (!formData.needs || formData.needs.length === 0)
    ) {
      // Skip step 4 (vendors) if no needs are selected
      setCurrentStep(5);
    } else {
      setCurrentStep((prev) => Math.min(prev + 1, 5));
    }

    // Scroll to top of modal when changing steps
    const modalContent = document.getElementById("modal-content");
    if (modalContent) {
      modalContent.scrollTop = 0;
    }
  };

  const handleBack = () => {
    // For Community Events, go back from final step to step 2
    if (formData.eventType === "Community Events" && currentStep === 98) {
      setCurrentStep(2);
    } else if (
      formData.eventType === "Commercial Event" &&
      currentStep === 99
    ) {
      // For Commercial Events, go back from success to contact form
      setCurrentStep(3);
    } else {
      setCurrentStep((prev) => Math.max(prev - 1, 1));
    }

    // Scroll to top of modal when changing steps
    const modalContent = document.getElementById("modal-content");
    if (modalContent) {
      modalContent.scrollTop = 0;
    }
  };

  const handleSkip = () => {
    setCurrentStep((prev) => Math.min(prev + 1, 5));
  };

  // Function to save event data to localStorage
  const saveEventToStorage = (data) => {
    localStorage.setItem("pendingEventData", JSON.stringify(data));

    // Store the current URL to redirect back after login
    const currentUrl = window.location.pathname + window.location.search;
    localStorage.setItem("redirectAfterLogin", currentUrl);
  };

  const [isProcessing, setIsProcessing] = useState(false);

  // Submit event function that can be called with specific data
  const submitEvent = async (dataToSubmit = null) => {
    const submissionData = dataToSubmit || formData;

    setFormData((prev) => ({ ...prev, completed: true }));

    // For Personal Events, show service request
    if (submissionData.eventType === "Personal Events") {
      try {
        setIsProcessing(true);
        const payload = new FormData();
        payload.append("eventName", submissionData.eventName);
        payload.append("eventType", submissionData.eventTypeDetail);
        payload.append("date", submissionData.date + " 00:00:00.000");
        payload.append("venue", submissionData.venue);
        payload.append("guestCount", submissionData.guests.toString());
        payload.append("budget", submissionData.budget.toString());
        payload.append("description", submissionData.description);
        payload.append("selectedEventNeeds", submissionData.needs);
        payload.append(
          "selectedEventIds",
          JSON.stringify(submissionData.vendors)
        );
        payload.append("businessid", submissionData.vendors);
        const addResponse = await addPersonalEvents(payload);
        successToast("Your Event has been successfully added");

        setShowServiceRequest(true);
      } catch (error) {
        console.error("Error while adding personal events", error);
        errorToast("Error while adding personal events");
      } finally {
        setIsProcessing(false);
      }
    } else if (submissionData.eventType === "Community Events") {
      try {
        setIsProcessing(true);
        const imageData = new FormData();
        imageData.append("image", submissionData.tempCommunityImage);
        const imageUrl = await handlePostRequest(
          "/auth/upload-image-to-s3",
          imageData,
          {},
          true
        );
        const payload = {
          name: submissionData.eventName,
          date: submissionData.date,
          eventstarttime: submissionData.startTime,
          eventendtime: submissionData.endTime,
          location: submissionData.venue,
          description: submissionData.description,
          cover_image: imageUrl.data[0],
          city: submissionData.city,
          state: submissionData.state,
          venue: submissionData.venue,
        };
        const addResponse = await addCommunityEvents(payload);
        successToast("Your Event Has Been Successfully Created");
        onClose();
      } catch (error) {
        console.error("Error while creating community events", error);
        errorToast("Error while creating community events");
      } finally {
        setIsProcessing(false);
      }
    } else if (submissionData.eventType === "Commercial Event") {
      try {
        setIsProcessing(true);
        const imageData = new FormData();
        imageData.append("image", submissionData.tempCommunityImage);
        const imageUrl = await handlePostRequest(
          "/auth/upload-image-to-s3",
          imageData,
          {},
          true
        );
        const payload = {
          name: submissionData.eventName,
          date: submissionData.date,
          eventstarttime: submissionData.startTime,
          eventendtime: submissionData.endTime,
          location: submissionData.venue,
          description: submissionData.description,
          cover_image: imageUrl.data[0],
          city: submissionData.city,
          state: submissionData.state,
          venue: submissionData.venue,
        };
        const addResponse = await addCommercialEvents(payload);
        successToast("Your Event Has Been Successfully Created");

        setCurrentStep(99);
      } catch (error) {
        console.error("Error while creating commercial events", error);
        errorToast("Error while creating commercial events");
      } finally {
        setIsProcessing(false);
      }
    }
  };

  // Update the handleDone function to check authentication
  const handleDone = async () => {
    // Check if user is authenticated
    if (!isAuthenticated()) {
      // Save form data to localStorage
      saveEventToStorage(formData);

      successToast(
        "Your progress has been saved! Please sign in to complete your event posting.",
        {
          position: "top-right",
          autoClose: 5000,
        }
      );

      // Close the modal
      onClose();

      // Navigate to sign in page
      navigate("/signin");
      return;
    }

    // If authenticated, proceed with submission
    await submitEvent();
  };

  const updateFormData = (data) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  const goToStep = (step) => {
    setCurrentStep(step);
    // Scroll to top of modal when changing steps
    const modalContent = document.getElementById("modal-content");
    if (modalContent) {
      modalContent.scrollTop = 0;
    }
  };

  const showLocationSearch = (initialValue = "") => {
    setModalState({ type: "location", initialValue });
  };

  const showEventTypeModal = (initialValue = "") => {
    setModalState({ type: "eventType", initialValue });
  };

  const closeModal = () => {
    setModalState({ type: "none" });
  };

  const handleSelectLocation = (location) => {
    updateFormData({ venue: location });
    closeModal();
  };

  const handleSelectEventType = (eventType) => {
    updateFormData({ eventTypeDetail: eventType });
    closeModal();
  };

  // Close modal on backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Get total steps based on event type
  const getTotalSteps = () => {
    if (formData.eventType === "Community Events") return 3;
    if (formData.eventType === "Commercial Event") return 4;
    return 5; // Default for Personal Events
  };

  // Update the renderContent function to include ServiceRequest for Personal Events
  const renderContent = () => {
    if (showServiceRequest && formData.eventType === "Personal Events") {
      return <ServiceRequest formData={formData} onClose={onClose} />;
    }

    if (modalState.type === "location") {
      return (
        <div className="p-4">
          <div className="flex items-center mb-4">
            <button onClick={closeModal} className="mr-3">
              <ArrowLeft size={20} />
            </button>
            <h2 className="text-lg font-medium">
              Search a location to add venue
            </h2>
          </div>

          <div className="mb-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search for a location"
                className="w-full px-4 py-3 pl-10 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent"
                autoFocus
              />
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M17.5 17.5L13.875 13.875"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>

          <button
            onClick={() => handleSelectLocation("Current Location")}
            className="flex items-center mb-6 text-[#7B189F]"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="mr-2"
            >
              <path
                d="M10 10.8333C11.3807 10.8333 12.5 9.71404 12.5 8.33333C12.5 6.95262 11.3807 5.83333 10 5.83333C8.61929 5.83333 7.5 6.95262 7.5 8.33333C7.5 9.71404 8.61929 10.8333 10 10.8333Z"
                stroke="#7B189F"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M15.8333 8.33333C15.8333 12.9357 10 16.6667 10 16.6667C10 16.6667 4.16667 12.9357 4.16667 8.33333C4.16667 6.56522 4.87053 4.86953 6.12087 3.61919C7.37121 2.36885 9.0669 1.66667 10.8333 1.66667C12.6014 1.66667 14.2971 2.36885 15.5474 3.61919C16.7978 4.86953 17.5 6.56522 17.5 8.33333V8.33333Z"
                stroke="#7B189F"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="font-medium">Use Current location</span>
          </button>

          <div className="text-xs font-medium text-gray-500 uppercase mb-2">
            Search Results
          </div>
          <div className="space-y-1">
            {[
              "Hill Street",
              "456 Hill Street",
              "789 Hill Street",
              "101 Hill Street",
            ].map((address, index) => (
              <button
                key={index}
                className="w-full flex items-start p-3 hover:bg-gray-50 rounded-lg transition-colors text-left"
                onClick={() => handleSelectLocation(address)}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="mr-3 mt-0.5 flex-shrink-0 text-gray-500"
                >
                  <path
                    d="M10 10.8333C11.3807 10.8333 12.5 9.71404 12.5 8.33333C12.5 6.95262 11.3807 5.83333 10 5.83333C8.61929 5.83333 7.5 6.95262 7.5 8.33333C7.5 9.71404 8.61929 10.8333 10 10.8333Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M15.8333 8.33333C15.8333 12.9357 10 16.6667 10 16.6667C10 16.6667 4.16667 12.9357 4.16667 8.33333C4.16667 6.56522 4.87053 4.86953 6.12087 3.61919C7.37121 2.36885 9.0669 1.66667 10.8333 1.66667C12.6014 1.66667 14.2971 2.36885 15.5474 3.61919C16.7978 4.86953 17.5 6.56522 17.5 8.33333V8.33333Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div>
                  <div className="font-medium">{address}</div>
                  <div className="text-sm text-gray-500">
                    {index === 0 && "Mission District, San Francisco, CA 94110"}
                    {index === 1 && "Sunset District, San Francisco, CA 94122"}
                    {index === 2 && "Nob Hill, San Francisco, CA 94109"}
                    {index === 3 && "Pacific Heights, San Francisco, CA 94115"}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (modalState.type === "eventType") {
      return (
        <div className="p-4">
          <div className="flex items-center mb-4">
            <button onClick={closeModal} className="mr-3">
              <ArrowLeft size={20} />
            </button>
            <h2 className="text-lg font-medium">Select event type</h2>
          </div>

          <div className="mb-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search type"
                className="w-full px-4 py-3 pl-10 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent"
                autoFocus
              />
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M17.5 17.5L13.875 13.875"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <div className="text-xs font-medium text-gray-500 uppercase mb-2">
              Celebrations
            </div>
            <div className="bg-gray-50 rounded-lg">
              {eventTypes &&
                eventTypes.map((type, index) => (
                  <button
                    key={type.id}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-100 transition-colors ${
                      index !== eventTypes.length - 1
                        ? "border-b border-gray-100"
                        : ""
                    }`}
                    onClick={() => handleSelectEventType(type.eventtypename)}
                  >
                    {type.eventtypename}
                  </button>
                ))}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="p-4">
        {currentStep === 1 && (
          <Step1EventType
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
          />
        )}

        {currentStep === 2 && (
          <Step2EventDetails
            formData={formData}
            updateFormData={updateFormData}
            onBack={handleBack}
            onNext={handleNext}
            onShowLocationSearch={showLocationSearch}
            onShowEventTypeModal={showEventTypeModal}
          />
        )}

        {/* Personal Events Flow */}
        {currentStep === 3 &&
          formData.eventType !== "Community Events" &&
          formData.eventType !== "Commercial Event" && (
            <Step3EventNeeds
              eventNeeds={eventNeeds}
              formData={formData}
              updateFormData={updateFormData}
              onBack={handleBack}
              onNext={handleNext}
              onSkip={handleSkip}
            />
          )}

        {currentStep === 4 &&
          formData.eventType !== "Community Events" &&
          formData.eventType !== "Commercial Event" && (
            <Step4Vendors
              businessData={business}
              eventNeeds={eventNeeds}
              formData={formData}
              updateFormData={updateFormData}
              onBack={handleBack}
              onNext={handleNext}
              onSkip={handleSkip}
            />
          )}

        {currentStep === 5 &&
          formData.eventType !== "Community Events" &&
          formData.eventType !== "Commercial Event" && (
            <Step5Summary
              formData={formData}
              onBack={handleBack}
              onDone={handleDone}
              goToStep={goToStep}
            />
          )}

        {/* Community Events Flow */}
        {currentStep === 98 && formData.eventType === "Community Events" && (
          <CommunityEventFinal
            formData={formData}
            updateFormData={updateFormData}
            onBack={handleBack}
            onDone={handleDone}
            goToStep={goToStep}
          />
        )}

        {/* Commercial Events Flow */}
        {currentStep === 3 && formData.eventType === "Commercial Event" && (
          <CommercialEventFinal
            formData={formData}
            updateFormData={updateFormData}
            onBack={handleBack}
            onDone={handleDone}
          />
        )}

        {currentStep === 99 && formData.eventType === "Commercial Event" && (
          <CommercialEventSuccess formData={formData} onClose={onClose} />
        )}
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 md:p-6"
      onClick={handleBackdropClick}
    >
      {isProcessing && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
          <Loader className="animate-spin" width={64} height={64} />
        </div>
      )}

      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md mx-auto overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Only show for non-success views */}
        {currentStep !== 99 && (
          <div className="flex items-center justify-between p-4 border-b">
            <h1 className="text-xl font-bold">
              {modalState.type === "location"
                ? "Search a location"
                : modalState.type === "eventType"
                ? "Select event type"
                : "Create Event"}
            </h1>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        )}

        {/* Content */}
        <div
          id="modal-content"
          className="overflow-y-auto max-h-[calc(100vh-150px)]"
        >
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
